/**
 * SoundManager Utility
 * Handles audio playback for PadelScoreCounter with robust mobile support.
 * Uses the Web Audio API and handles AudioContext resumption on user interaction.
 */

class SoundManager {
    private ctx: AudioContext | null = null;
    private isUnlocked: boolean = false;
    private lastPlayTime: number = 0;

    constructor() {
        // Lazy initialization handled in unlock/play
    }

    private getContext(): AudioContext {
        if (!this.ctx) {
            const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
            this.ctx = new AudioContextClass();
        }
        return this.ctx;
    }

    /**
     * Attempts to unlock the AudioContext.
     * Should be called on the first user interaction (click/touch).
     */
    public unlock() {
        if (this.isUnlocked) return;

        const ctx = this.getContext();
        if (ctx.state === 'suspended') {
            ctx.resume().then(() => {
                this.isUnlocked = true;
                console.log('[SoundManager] AudioContext resumed successfully.');
            }).catch(err => {
                console.warn('[SoundManager] Failed to resume AudioContext:', err);
            });
        } else {
            this.isUnlocked = true;
        }
    }

    /**
     * Plays a sound based on the type.
     */
    public play(type: 'point' | 'game' | 'set' | 'test') {
        // Throttle slightly to prevent overlap chaos if multiple events fire
        const now = Date.now();
        if (now - this.lastPlayTime < 50) return;
        this.lastPlayTime = now;

        try {
            const ctx = this.getContext();

            // Auto-resume if needed (fallback)
            if (ctx.state === 'suspended') {
                ctx.resume();
            }

            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);

            const t = ctx.currentTime;

            if (type === 'point') {
                // Short, distinct "Pop" / Tennis ball hit sound simulation
                // Sine wave sweeping down fast
                osc.type = 'sine';
                osc.frequency.setValueAtTime(800, t);
                osc.frequency.exponentialRampToValueAtTime(100, t + 0.15);

                gain.gain.setValueAtTime(0.5, t);
                gain.gain.exponentialRampToValueAtTime(0.01, t + 0.15);

                osc.start(t);
                osc.stop(t + 0.15);

            } else if (type === 'game') {
                // "Success" chord - Major Triad (C5 - E5 - G5)
                this.playNote(ctx, 523.25, t, 0.4, 'triangle'); // C5
                this.playNote(ctx, 659.25, t + 0.05, 0.4, 'triangle'); // E5
                this.playNote(ctx, 783.99, t + 0.1, 0.6, 'triangle'); // G5
                // Small celebration rhythm

            } else if (type === 'set') {
                // "Victory" Fanfare - Arpeggio up
                // A Major: A4, C#5, E5, A5
                const start = t;
                const dur = 0.15;
                this.playNote(ctx, 440.00, start, 0.5, 'square');
                this.playNote(ctx, 554.37, start + dur, 0.5, 'square');
                this.playNote(ctx, 659.25, start + dur * 2, 0.5, 'square');
                this.playNote(ctx, 880.00, start + dur * 3, 1.0, 'square'); // Long last note

            } else if (type === 'test') {
                // Simple blip for testing
                osc.type = 'triangle';
                osc.frequency.setValueAtTime(440, t);
                gain.gain.setValueAtTime(0.1, t);
                gain.gain.linearRampToValueAtTime(0, t + 0.1);
                osc.start(t);
                osc.stop(t + 0.1);
            }

        } catch (e) {
            console.error('[SoundManager] Error playing sound:', e);
        }
    }

    private playNote(ctx: AudioContext, freq: number, startTime: number, duration: number = 0.1, type: OscillatorType = 'sine') {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.type = type;
        osc.frequency.setValueAtTime(freq, startTime);

        // ADSR Envelope-ish
        gain.gain.setValueAtTime(0.01, startTime);
        gain.gain.linearRampToValueAtTime(0.3, startTime + 0.02); // Attack
        gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration); // Decay/Release

        osc.start(startTime);
        osc.stop(startTime + duration);
    }
}

export const soundManager = new SoundManager();
